import React, { useState, useContext, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { authHeader } from "../../utils/authHeader";
import AddIcon from "@mui/icons-material/Add";
import { AuthContext } from "../../context/AuthContext";
import { createPoll, POLL_COLORS } from "../../services/pollsService";
import { pedir } from "../../utils/respuestaApi";
import { puedeAdministrarEspacio } from "../../utils/permisosEspacio";
import "./polls.scss";

import { API_BASE_URL as API, urlDeArchivo } from "../../config";

// `spaceId` es solo la preselección (p. ej. si el modal se abre desde
// dentro de un espacio ya abierto): el espacio real de la encuesta siempre
// lo elige la persona en este mismo formulario, con el mismo selector
// visual que usa "Compartir" para publicar en un espacio — así se puede
// crear la encuesta desde cualquier parte, no solo estando dentro de él.
const CreatePollModal = ({ spaceId, onClose, onCreated }) => {
  const { currentUser } = useContext(AuthContext);
  const [question, setQuestion] = useState("");
  const [description, setDescription] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [color, setColor] = useState(POLL_COLORS[0]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successSpaceName, setSuccessSpaceName] = useState("");

  const [spaces, setSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState(spaceId || "");
  const [spacesLoading, setSpacesLoading] = useState(true);

  // Fijar depende del espacio ELEGIDO en el formulario: un encargado solo
  // puede fijar en los suyos (el servidor aplica la misma regla).
  const puedeFijar = puedeAdministrarEspacio(currentUser, selectedSpaceId);

  useEffect(() => {
    let cancelled = false;
    pedir(`${API}/api/spaces`, { headers: { ...authHeader() } }, "No se pudieron cargar los espacios")
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.spaces) ? data.spaces : [];
        setSpaces(list);
        setSelectedSpaceId((prev) => {
          if (prev && list.some((s) => String(s.id) === String(prev))) return prev;
          return list[0]?.id || "";
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // Antes esto solo iba a la consola: en pantalla quedaba un escueto
        // "No hay espacios disponibles" con el botón deshabilitado y sin
        // ninguna pista de que el problema era la sesión o el servidor.
        console.error("Error cargando espacios:", err);
        setError(err.message || "No se pudieron cargar los espacios");
      })
      .finally(() => {
        if (!cancelled) setSpacesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateOption = (idx, value) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? value : o)));
  };

  const addOption = () => {
    if (options.length >= 8) return;
    setOptions((prev) => [...prev, ""]);
  };

  const removeOption = (idx) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
    if (!selectedSpaceId) {
      setError("Elige el espacio donde va la encuesta");
      return;
    }
    if (!question.trim()) {
      setError("Escribe la pregunta de la encuesta");
      return;
    }
    if (cleanOptions.length < 2) {
      setError("Agrega al menos 2 opciones de respuesta");
      return;
    }

    setSubmitting(true);
    try {
      const poll = await createPoll({
        spaceId: selectedSpaceId,
        question: question.trim(),
        description: description.trim(),
        options: cleanOptions,
        color,
        allowMultiple,
        isPinned: puedeFijar && isPinned
      });
      // Confirmación breve antes de cerrar: sobre todo útil cuando la
      // encuesta se creó para un espacio distinto al que se está viendo
      // (ahí no aparece ninguna tarjeta nueva en pantalla, así que sin
      // esto no habría ninguna señal de que sí se guardó).
      const spaceName = spaces.find((s) => String(s.id) === String(selectedSpaceId))?.name || "el espacio elegido";
      setSuccessSpaceName(spaceName);
      setTimeout(() => {
        onCreated(poll);
        onClose();
      }, 1100);
    } catch (err) {
      setError(err.message || "No se pudo crear la encuesta");
      // Si lo que falló fue la sesión (el token dura 8 horas), de nada sirve
      // volver a intentarlo: se limpia y se manda a iniciar sesión de nuevo,
      // que es lo único que destraba el caso.
      if (err.sesionExpirada) {
        localStorage.removeItem("token");
        setTimeout(() => { window.location.href = "/login"; }, 2500);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="poll-modal-overlay" onClick={onClose}>
      <div className="poll-modal" onClick={(e) => e.stopPropagation()}>
        <div className="poll-modal-header">
          <h3>Nueva encuesta</h3>
          <button className="poll-modal-close" onClick={onClose} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {successSpaceName ? (
          <div className="poll-form-success">
            <span className="poll-form-success-check">✓</span>
            Encuesta creada en <strong>{successSpaceName}</strong>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="poll-form">
          <div className="poll-form-label poll-space-picker">
            Espacio
            {spacesLoading ? (
              <span className="poll-space-loading">Cargando espacios...</span>
            ) : spaces.length === 0 ? (
              <span className="poll-space-loading">No hay espacios disponibles</span>
            ) : (
              <div className="poll-space-list">
                {spaces.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    className={`poll-space-item ${String(selectedSpaceId) === String(s.id) ? "selected" : ""}`}
                    onClick={() => setSelectedSpaceId(s.id)}
                  >
                    {s.image ? (
                      <img src={urlDeArchivo(s.image)} alt={s.name} />
                    ) : (
                      <span className="poll-space-fallback">SP</span>
                    )}
                    <span>{s.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="poll-form-label">
            Pregunta
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="¿Qué quieres preguntar?"
              maxLength={500}
              autoFocus
            />
          </label>

          <label className="poll-form-label">
            Descripción (opcional)
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Un poco más de contexto..."
              maxLength={300}
            />
          </label>

          <div className="poll-form-label">
            Opciones de respuesta
            <div className="poll-options-editor">
              {options.map((opt, idx) => (
                <div className="poll-option-input-row" key={idx}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    placeholder={`Opción ${idx + 1}`}
                    maxLength={255}
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      className="poll-remove-option"
                      onClick={() => removeOption(idx)}
                      aria-label="Quitar opción"
                    >
                      <CloseIcon fontSize="inherit" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 8 && (
              <button type="button" className="poll-add-option" onClick={addOption}>
                <AddIcon fontSize="inherit" /> Agregar opción
              </button>
            )}
          </div>

          <div className="poll-form-label">
            Color de la encuesta
            <div className="poll-color-picker">
              {POLL_COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  className={`poll-color-swatch ${color === c ? "selected" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>

          <label className="poll-checkbox-row">
            <input
              type="checkbox"
              checked={allowMultiple}
              onChange={(e) => setAllowMultiple(e.target.checked)}
            />
            Permitir seleccionar varias opciones
          </label>

          {puedeFijar && (
            <label className="poll-checkbox-row">
              <input
                type="checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
              />
              Fijar arriba del muro. Sin marcar, la encuesta aparece como una
              publicación más, en orden de fecha.
            </label>
          )}

          {error && <p className="poll-form-error">{error}</p>}

          <div className="poll-form-actions">
            <button type="button" className="poll-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="poll-btn-primary" disabled={submitting || !selectedSpaceId}>
              {submitting ? "Creando..." : "Crear encuesta"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default CreatePollModal;
